import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// TR (built-in)
import trCommon from './locales/tr/common.json';
import trLanding from './locales/tr/landing.json';
import trAuth from './locales/tr/auth.json';
import trDashboard from './locales/tr/dashboard.json';
import trMemberDashboard from './locales/tr/memberDashboard.json';
import trReservations from './locales/tr/reservations.json';
import trMembers from './locales/tr/members.json';
import trTrainers from './locales/tr/trainers.json';
import trSettings from './locales/tr/settings.json';
import trPayments from './locales/tr/payments.json';
import trRooms from './locales/tr/rooms.json';
import trMessaging from './locales/tr/messaging.json';
import trBusiness from './locales/tr/business.json';
import trPackages from './locales/tr/packages.json';
import trReports from './locales/tr/reports.json';
import trEvents from './locales/tr/events.json';
import trEmployees from './locales/tr/employees.json';
import trSurveys from './locales/tr/surveys.json';
import trAdmin from './locales/tr/admin.json';
import trQr from './locales/tr/qr.json';
import trEvaluations from './locales/tr/evaluations.json';

// EN (built-in)
import enCommon from './locales/en/common.json';
import enLanding from './locales/en/landing.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enMemberDashboard from './locales/en/memberDashboard.json';
import enReservations from './locales/en/reservations.json';
import enMembers from './locales/en/members.json';
import enTrainers from './locales/en/trainers.json';
import enSettings from './locales/en/settings.json';
import enPayments from './locales/en/payments.json';
import enRooms from './locales/en/rooms.json';
import enMessaging from './locales/en/messaging.json';
import enBusiness from './locales/en/business.json';
import enPackages from './locales/en/packages.json';
import enReports from './locales/en/reports.json';
import enEvents from './locales/en/events.json';
import enEmployees from './locales/en/employees.json';
import enSurveys from './locales/en/surveys.json';
import enAdmin from './locales/en/admin.json';
import enQr from './locales/en/qr.json';
import enEvaluations from './locales/en/evaluations.json';

const NAMESPACES = ['common', 'landing', 'auth', 'dashboard', 'memberDashboard', 'reservations', 'members', 'trainers', 'settings', 'payments', 'rooms', 'messaging', 'business', 'packages', 'reports', 'events', 'employees', 'surveys', 'admin', 'qr', 'evaluations'];

const resources = {
  tr: {
    common: trCommon,
    landing: trLanding,
    auth: trAuth,
    dashboard: trDashboard,
    memberDashboard: trMemberDashboard,
    reservations: trReservations,
    members: trMembers,
    trainers: trTrainers,
    settings: trSettings,
    payments: trPayments,
    rooms: trRooms,
    messaging: trMessaging,
    business: trBusiness,
    packages: trPackages,
    reports: trReports,
    events: trEvents,
    employees: trEmployees,
    surveys: trSurveys,
    admin: trAdmin,
    qr: trQr,
    evaluations: trEvaluations,
  },
  en: {
    common: enCommon,
    landing: enLanding,
    auth: enAuth,
    dashboard: enDashboard,
    memberDashboard: enMemberDashboard,
    reservations: enReservations,
    members: enMembers,
    trainers: enTrainers,
    settings: enSettings,
    payments: enPayments,
    rooms: enRooms,
    messaging: enMessaging,
    business: enBusiness,
    packages: enPackages,
    reports: enReports,
    events: enEvents,
    employees: enEmployees,
    surveys: enSurveys,
    admin: enAdmin,
    qr: enQr,
    evaluations: enEvaluations,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'tr',
    defaultNS: 'common',
    ns: NAMESPACES,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: 'pbstudio_language',
      caches: ['localStorage'],
    },
  })
  .then(() => {
    // Force apply language from localStorage after init completes
    const storedLang = localStorage.getItem('pbstudio_language');
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }
  });

/**
 * Dynamically load a language from the backend API.
 * Used for languages added via admin Translation Management UI.
 * Built-in languages (tr, en) are bundled statically.
 */
export async function loadDynamicLanguage(langCode) {
  if (langCode === 'tr' || langCode === 'en') return true;
  
  if (i18n.hasResourceBundle(langCode, 'common')) return true;

  const API = process.env.REACT_APP_BACKEND_URL;
  try {
    const promises = NAMESPACES.map(ns =>
      fetch(`${API}/api/translations/public/${langCode}/${ns}`)
        .then(r => r.ok ? r.json() : {})
    );
    const results = await Promise.all(promises);
    NAMESPACES.forEach((ns, idx) => {
      i18n.addResourceBundle(langCode, ns, results[idx], true, true);
    });
    return true;
  } catch (err) {
    console.error(`Failed to load language: ${langCode}`, err);
    return false;
  }
}

export { NAMESPACES };
export default i18n;
